import React from 'react'
import { Copy, Check, ExternalLink, ChevronDown, ChevronUp, AlertCircle, Code2 } from 'lucide-react'

interface EndpointSection {
  title: string
  endpoints: APIEndpointDoc[]
}

interface APIEndpointDoc {
  id: string
  title: string
  endpoint: string
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  description: string
  headers?: Record<string, string>
  requestBody?: any
  queryParams?: Record<string, string>
  pathParams?: Record<string, string>
  sampleResponse?: any
  errorResponses?: ErrorResponse[]
  notes?: string[]
  example?: string
}

interface ErrorResponse {
  code: number
  message: string
  description: string
}

interface APIDocumentationProps {
  endpoint?: string
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  headers?: Record<string, string>
  requestBody?: any
  sampleResponse?: any
  description?: string
  className?: string
  // New comprehensive props
  sections?: EndpointSection[]
  apiKey?: string
  apiSecret?: string
  baseUrl?: string
  isComprehensive?: boolean
}

const APIDocumentation: React.FC<APIDocumentationProps> = ({
  endpoint,
  method,
  headers = {},
  requestBody,
  sampleResponse,
  description,
  className = '',
  sections = [],
  apiKey = 'pk_your_api_key',
  apiSecret = 'sk_your_api_secret',
  baseUrl = 'https://api.pexiblock.com/api',
  isComprehensive = false,
}) => {
  const [copiedItems, setCopiedItems] = React.useState<Set<string>>(new Set())
  const [expandedEndpoints, setExpandedEndpoints] = React.useState<Set<string>>(new Set())

  const copyToClipboard = async (text: string, itemId: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedItems(prev => new Set(prev).add(itemId))
      setTimeout(() => {
        setCopiedItems(prev => {
          const newSet = new Set(prev)
          newSet.delete(itemId)
          return newSet
        })
      }, 2000)
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
  }

  const toggleEndpoint = (id: string) => {
    setExpandedEndpoints(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const formatJson = (obj: any) => {
    return JSON.stringify(obj, null, 2)
  }

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'GET':
        return 'bg-green-500/20 text-green-400 border-green-500/30'
      case 'POST':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30'
      case 'PUT':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
      case 'DELETE':
        return 'bg-red-500/20 text-red-400 border-red-500/30'
      case 'PATCH':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30'
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    }
  }

  const renderSingleEndpoint = () => (
    <div className="bg-gray-900/50 border border-gray-700/50 rounded-lg p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <Code2 className="h-5 w-5 text-blue-400" />
        <h3 className="text-lg font-semibold text-white">API Endpoint</h3>
      </div>

      {/* Description */}
      {description && <div className="text-gray-300 text-sm leading-relaxed">{description}</div>}

      {/* Endpoint */}
      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-300">Endpoint:</span>
          <span className={`px-2 py-1 rounded text-xs font-mono border ${getMethodColor(method || 'GET')}`}>
            {method}
          </span>
        </div>
        <div className="flex items-center bg-gray-800/50 border border-gray-600/50 rounded p-3 space-x-2">
          <code className="font-mono text-sm text-gray-200 flex-1 overflow-x-auto">{endpoint}</code>
          <button
            onClick={() => copyToClipboard(endpoint || '', 'endpoint')}
            className="opacity-0 hover:opacity-100 transition-opacity p-1 hover:bg-gray-700/50 rounded flex-shrink-0"
          >
            {copiedItems.has('endpoint') ? (
              <Check className="h-4 w-4 text-green-400" />
            ) : (
              <Copy className="h-4 w-4 text-gray-400" />
            )}
          </button>
        </div>
      </div>

      {/* Headers */}
      {Object.keys(headers).length > 0 && (
        <div className="space-y-2">
          <span className="text-sm font-medium text-gray-300">Headers:</span>
          <div className="bg-gray-800/50 border border-gray-600/50 rounded p-3 space-y-2">
            {Object.entries(headers).map(([key, value], index) => (
              <div key={index} className="flex items-center justify-between group">
                <div className="font-mono text-sm text-gray-200 flex-1">
                  <span className="text-blue-400">{key}:</span>
                  <span className="text-green-400 ml-2">"{value}"</span>
                </div>
                <button
                  onClick={() => copyToClipboard(`${key}: ${value}`, `header-${index}`)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-700/50 rounded flex-shrink-0"
                >
                  {copiedItems.has(`header-${index}`) ? (
                    <Check className="h-4 w-4 text-green-400" />
                  ) : (
                    <Copy className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Request Body */}
      {requestBody && (
        <div className="space-y-2">
          <span className="text-sm font-medium text-gray-300">Request Body Example:</span>
          <div className="relative">
            <div className="bg-gray-800/50 border border-gray-600/50 rounded p-3">
              <pre className="text-sm text-gray-200 overflow-x-auto">
                <code>{formatJson(requestBody)}</code>
              </pre>
            </div>
            <button
              onClick={() => copyToClipboard(formatJson(requestBody), 'request-body')}
              className="absolute top-2 right-2 p-2 hover:bg-gray-700/50 rounded transition-colors"
            >
              {copiedItems.has('request-body') ? (
                <Check className="h-4 w-4 text-green-400" />
              ) : (
                <Copy className="h-4 w-4 text-gray-400" />
              )}
            </button>
          </div>
        </div>
      )}

      {/* Sample Response */}
      {sampleResponse && (
        <div className="space-y-2">
          <span className="text-sm font-medium text-gray-300">Sample Response:</span>
          <div className="relative">
            <div className="bg-gray-800/50 border border-gray-600/50 rounded p-3">
              <pre className="text-sm text-gray-200 overflow-x-auto">
                <code>{formatJson(sampleResponse)}</code>
              </pre>
            </div>
            <button
              onClick={() => copyToClipboard(formatJson(sampleResponse), 'response')}
              className="absolute top-2 right-2 p-2 hover:bg-gray-700/50 rounded transition-colors"
            >
              {copiedItems.has('response') ? (
                <Check className="h-4 w-4 text-green-400" />
              ) : (
                <Copy className="h-4 w-4 text-gray-400" />
              )}
            </button>
          </div>
        </div>
      )}

      {/* Usage Instructions */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-2">
          <AlertCircle className="h-4 w-4 text-blue-400" />
          <span className="text-sm font-medium text-blue-300">Authentication Required</span>
        </div>
        <div className="text-sm text-blue-200 space-y-1">
          <p>1. Include the API key in the <code className="bg-blue-500/20 px-1 rounded text-blue-100">X-API-Key</code> header</p>
          <p>2. Include the API secret in the <code className="bg-blue-500/20 px-1 rounded text-blue-100">X-API-Secret</code> header</p>
          <p>3. Set the <code className="bg-blue-500/20 px-1 rounded text-blue-100">Content-Type</code> to <code className="bg-blue-500/20 px-1 rounded text-blue-100">application/json</code></p>
          <p>4. Send the request with the appropriate method and endpoint</p>
        </div>
      </div>
    </div>
  )

  const renderComprehensiveDocumentation = () => (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="bg-gray-900/50 border border-gray-700/50 rounded-lg p-6 space-y-4">
        <div className="flex items-center space-x-3">
          <ExternalLink className="h-5 w-5 text-blue-400" />
          <h1 className="text-2xl font-bold text-white">PexiBlock Public API Documentation</h1>
        </div>
        <p className="text-gray-300">
          Complete reference guide for integrating with the PexiBlock Payment API.
        </p>
      </div>

      {/* Quick Start Section */}
      <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-6 space-y-4">
        <h2 className="text-lg font-semibold text-green-300 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5" />
          <span>Quick Start</span>
        </h2>
        <div className="space-y-3 text-sm text-green-200">
          <div>
            <p className="font-medium text-green-300 mb-2">1. Get Your API Keys</p>
            <p>Navigate to your dashboard and generate API credentials from the Developer section.</p>
          </div>
          <div>
            <p className="font-medium text-green-300 mb-2">2. Set Up Authentication</p>
            <p>All requests require <code className="bg-green-500/20 px-1 rounded">X-API-Key</code> and <code className="bg-green-500/20 px-1 rounded">X-API-Secret</code> headers.</p>
          </div>
          <div>
            <p className="font-medium text-green-300 mb-2">3. Create Your First Payment</p>
            <p>Use the Create Payment endpoint with customer details and amount to initiate a transaction.</p>
          </div>
        </div>
      </div>

      {/* Base URL */}
      <div className="bg-gray-900/50 border border-gray-700/50 rounded-lg p-6 space-y-3">
        <h3 className="text-sm font-medium text-gray-300">Base URL</h3>
        <div className="flex items-center bg-gray-800/50 border border-gray-600/50 rounded p-3 space-x-2">
          <code className="font-mono text-sm text-gray-200 flex-1">{baseUrl}</code>
          <button
            onClick={() => copyToClipboard(baseUrl, 'base-url')}
            className="p-1 hover:bg-gray-700/50 rounded"
          >
            {copiedItems.has('base-url') ? (
              <Check className="h-4 w-4 text-green-400" />
            ) : (
              <Copy className="h-4 w-4 text-gray-400" />
            )}
          </button>
        </div>
      </div>

      {/* Endpoints by Section */}
      {sections.map((section, sectionIndex) => (
        <div key={sectionIndex} className="bg-gray-900/50 border border-gray-700/50 rounded-lg p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white">{section.title}</h2>

          <div className="space-y-3">
            {section.endpoints.map((endpointDoc, endpointIndex) => {
              const endpointKey = `${sectionIndex}-${endpointIndex}`
              const isExpanded = expandedEndpoints.has(endpointKey)

              return (
                <div
                  key={endpointKey}
                  className="border border-gray-600/50 rounded-lg overflow-hidden bg-gray-800/30 hover:bg-gray-800/50 transition-colors"
                >
                  {/* Endpoint Header */}
                  <button
                    onClick={() => toggleEndpoint(endpointKey)}
                    className="w-full p-4 flex items-center justify-between hover:bg-gray-700/20 transition-colors"
                  >
                    <div className="flex items-center space-x-3 text-left flex-1">
                      <span className={`px-2 py-1 rounded text-xs font-mono border ${getMethodColor(endpointDoc.method)}`}>
                        {endpointDoc.method}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{endpointDoc.title}</p>
                        <p className="text-xs text-gray-400 truncate font-mono">{endpointDoc.endpoint}</p>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    )}
                  </button>

                  {/* Endpoint Details */}
                  {isExpanded && (
                    <div className="border-t border-gray-600/50 p-4 space-y-4 bg-gray-900/30">
                      {/* Description */}
                      <div>
                        <p className="text-sm text-gray-300 leading-relaxed">{endpointDoc.description}</p>
                      </div>

                      {/* Full Endpoint URL */}
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-gray-400">Full URL</p>
                        <div className="flex items-center bg-gray-900/50 border border-gray-600/50 rounded p-2 space-x-2">
                          <code className="font-mono text-xs text-gray-300 flex-1 overflow-x-auto">
                            {endpointDoc.endpoint}
                          </code>
                          <button
                            onClick={() => copyToClipboard(endpointDoc.endpoint, `url-${endpointKey}`)}
                            className="p-1 hover:bg-gray-700/50 rounded flex-shrink-0"
                          >
                            {copiedItems.has(`url-${endpointKey}`) ? (
                              <Check className="h-3 w-3 text-green-400" />
                            ) : (
                              <Copy className="h-3 w-3 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Path Parameters */}
                      {endpointDoc.pathParams && Object.keys(endpointDoc.pathParams).length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-gray-400">Path Parameters</p>
                          <div className="bg-gray-900/50 border border-gray-600/50 rounded p-3 space-y-2">
                            {Object.entries(endpointDoc.pathParams).map(([key, value], idx) => (
                              <div key={idx} className="text-xs">
                                <span className="text-blue-400">{key}:</span>
                                <span className="text-gray-300 ml-2">{value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Query Parameters */}
                      {endpointDoc.queryParams && Object.keys(endpointDoc.queryParams).length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-gray-400">Query Parameters</p>
                          <div className="bg-gray-900/50 border border-gray-600/50 rounded p-3 space-y-2">
                            {Object.entries(endpointDoc.queryParams).map(([key, value], idx) => (
                              <div key={idx} className="text-xs">
                                <span className="text-blue-400">{key}:</span>
                                <span className="text-gray-300 ml-2">{value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Request Body */}
                      {endpointDoc.requestBody && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-gray-400">Request Body</p>
                          <div className="relative">
                            <pre className="bg-gray-900/50 border border-gray-600/50 rounded p-3 text-xs text-gray-300 overflow-x-auto">
                              <code>{formatJson(endpointDoc.requestBody)}</code>
                            </pre>
                            <button
                              onClick={() => copyToClipboard(formatJson(endpointDoc.requestBody), `body-${endpointKey}`)}
                              className="absolute top-2 right-2 p-1 hover:bg-gray-700/50 rounded"
                            >
                              {copiedItems.has(`body-${endpointKey}`) ? (
                                <Check className="h-3 w-3 text-green-400" />
                              ) : (
                                <Copy className="h-3 w-3 text-gray-400" />
                              )}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Sample Response */}
                      {endpointDoc.sampleResponse && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-gray-400">Sample Response</p>
                          <div className="relative">
                            <pre className="bg-gray-900/50 border border-gray-600/50 rounded p-3 text-xs text-gray-300 overflow-x-auto">
                              <code>{formatJson(endpointDoc.sampleResponse)}</code>
                            </pre>
                            <button
                              onClick={() => copyToClipboard(formatJson(endpointDoc.sampleResponse), `response-${endpointKey}`)}
                              className="absolute top-2 right-2 p-1 hover:bg-gray-700/50 rounded"
                            >
                              {copiedItems.has(`response-${endpointKey}`) ? (
                                <Check className="h-3 w-3 text-green-400" />
                              ) : (
                                <Copy className="h-3 w-3 text-gray-400" />
                              )}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Error Responses */}
                      {endpointDoc.errorResponses && endpointDoc.errorResponses.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-gray-400">Possible Error Responses</p>
                          <div className="space-y-2">
                            {endpointDoc.errorResponses.map((error, idx) => (
                              <div key={idx} className="bg-red-500/10 border border-red-500/20 rounded p-2">
                                <p className="text-xs font-mono text-red-300">
                                  {error.code}: {error.message}
                                </p>
                                <p className="text-xs text-red-200 mt-1">{error.description}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Notes */}
                      {endpointDoc.notes && endpointDoc.notes.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-gray-400">Notes</p>
                          <div className="space-y-1">
                            {endpointDoc.notes.map((note, idx) => (
                              <p key={idx} className="text-xs text-gray-300">• {note}</p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      ))}

      {/* Authentication Section */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-6 space-y-4">
        <div className="flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-blue-400" />
          <h2 className="text-lg font-semibold text-blue-300">Authentication</h2>
        </div>
        <div className="text-sm text-blue-200 space-y-3">
          <p>All API endpoints require authentication using API keys. Include the following headers in every request:</p>
          <div className="bg-blue-900/30 border border-blue-500/30 rounded p-3 space-y-1 font-mono text-xs">
            <p><span className="text-blue-400">X-API-Key:</span> <span className="text-green-400">{apiKey}</span></p>
            <p><span className="text-blue-400">X-API-Secret:</span> <span className="text-green-400">{apiSecret}</span></p>
            <p><span className="text-blue-400">Content-Type:</span> <span className="text-green-400">application/json</span></p>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className={className}>
      {isComprehensive ? renderComprehensiveDocumentation() : renderSingleEndpoint()}
    </div>
  )
}

export default APIDocumentation
export type { EndpointSection, APIEndpointDoc, ErrorResponse, APIDocumentationProps }
